import { gql } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select, { SingleValue } from 'react-select';
import FlagDE from '~components/ui/icons/FlagDE.png';
import FlagES from '~components/ui/icons/FlagES.png';
import FlagFR from '~components/ui/icons/FlagFR.png';
import FlagIT from '~components/ui/icons/FlagIT.png';
import FlagJA from '~components/ui/icons/FlagJA.png';
import FlagUK from '~components/ui/icons/FlagUK.png';
import { components } from '~components/ui/Select';
import { VerticalTabContent } from '~components/ui/VerticalTabs';
import { fromError } from '~utils/errors';
import { useAuthentication } from './../../auth/useAuthentication';
import { useSelfProfileUpdatesMutation } from './__generated__/profileSettings.graphql';

export function ProfileSettings() {
  const toast = useToast();
  const { t, i18n } = useTranslation();
  const languageOptions = [
    {
      value: 'en-GB',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img src={FlagUK} alt="UK Flag" width="20" />
          {t('english')}
        </div>
      ),
    },
    {
      value: 'fr',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img src={FlagFR} alt="French Flag" width="20" />
          {t('french')}
        </div>
      ),
    },
    {
      value: 'de',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img src={FlagDE} alt="German Flag" width="20" />
          {t('german')}
        </div>
      ),
    },
    {
      value: 'it',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img src={FlagIT} alt="Italian Flag" width="20" />
          {t('italian')}
        </div>
      ),
    },
    {
      value: 'es',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img src={FlagES} alt="Spanish Flag" width="20" />
          {t('spanish')}
        </div>
      ),
    },
    {
      value: 'ja',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img src={FlagJA} alt="Japan Flag" width="20" />
          {t('japanese')}
        </div>
      ),
    },
  ];
  const { user } = useAuthentication();
  const [locale, setLocale] = useState<string>(
    localStorage.getItem('locale') || user.locale || 'en-GB',
  );
  const [tempUnit, setTempUnit] = useState<string>(
    localStorage.getItem('tempUnit') || user.tempUnit || 'Celsius',
  );
  const tempUnitOptions = [
    { value: 'Celsius', label: <div>{t('metricSystem')}</div> },
    { value: 'Fahrenheit', label: <div>{t('imperialSystem')}</div> },
  ];

  const [SelfProfileUpdateMutation] = useSelfProfileUpdatesMutation();

  const handleChange = useCallback(
    async (selectedOption: SingleValue<{ value: string; label: JSX.Element }>) => {
      if (!selectedOption) return;
      const selectedLanguage = selectedOption.value;

      try {
        await SelfProfileUpdateMutation({
          variables: {
            input: {
              locale: selectedLanguage,
            },
          },
        });
        await i18n.changeLanguage(selectedLanguage);
        setLocale(selectedLanguage);
        localStorage.setItem('locale', selectedLanguage);
      } catch (err) {
        toast({
          status: 'error',
          title: t('languageUpdateErr'),
          description: fromError(err, 'UpdateLanguage'),
        });
      }
    },
    [SelfProfileUpdateMutation, toast, i18n, t],
  );

  const handleTempUnitChange = useCallback(
    async (selectedOption: SingleValue<{ value: string; label: JSX.Element }>) => {
      if (!selectedOption) return;
      const selectedUnit = selectedOption.value;

      try {
        await SelfProfileUpdateMutation({
          variables: {
            input: {
              tempUnit: selectedUnit,
            },
          },
        });
        setTempUnit(selectedUnit);
        localStorage.setItem('tempUnit', selectedUnit);
      } catch (err) {
        toast({
          status: 'error',
          title: t('tempUnitUpdateErr'),
          description: fromError(err, 'UpdateTempUnit'),
        });
      }
    },
    [SelfProfileUpdateMutation, toast, t],
  );

  return (
    <VerticalTabContent title={t('profile')}>
      <label style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>
        {t('changeLanguage')}
      </label>
      <Select
        value={languageOptions.find((option) => option.value === locale)}
        options={languageOptions}
        components={{
          ...components,
        }}
        styles={{
          control: (base) => ({
            ...base,
            width: '300px',
            minWidth: '200px',
            maxWidth: '100%',
          }),
          menu: (base) => ({
            ...base,
            width: '300px',
            minWidth: '200px',
          }),
        }}
        onChange={handleChange}
        isMulti={false}
        escapeClearsValue={true}
        isSearchable={false}
        isClearable={false}
        menuPlacement="auto"
      />
      <>
        <label
          style={{ fontWeight: 'bold', marginBottom: '8px', marginTop: '26px', display: 'block' }}
        >
          {t('preferredTempUnit')}
        </label>
        <Select
          value={tempUnitOptions.find((option) => option.value === tempUnit)}
          options={tempUnitOptions}
          components={{
            ...components,
          }}
          styles={{
            control: (base) => ({
              ...base,
              width: '300px',
              minWidth: '200px',
              maxWidth: '100%',
            }),
            menu: (base) => ({
              ...base,
              width: '300px',
              minWidth: '200px',
            }),
          }}
          onChange={handleTempUnitChange}
          isMulti={false}
          escapeClearsValue={true}
          isSearchable={false}
          isClearable={false}
          menuPlacement="auto"
        />
      </>
    </VerticalTabContent>
  );
}

ProfileSettings.graphql = {
  mutations: {
    SelfProfileUpdates: gql`
      mutation SelfProfileUpdates($input: SelfProfileUpdateInput!) {
        selfProfileUpdate(input: $input) {
          user {
            locale
            tempUnit
          }
        }
      }
    `,
  },
};
